//Copyright - Kyley Harris (2021) - All rights to copy and reuse allowed
import { write } from '../language/lang';
import * as db from './indexed-db';
import { GetOptions } from './indexed-db';
import { alertAction } from '../components/dialogs/agwa-confirm-dialog';

// wrap indexed db in a handler.
function information(title: string, message: string) {
  void alertAction({
    title,
    message,
    confirmText: write`OK`,
    variant: 'warning',
  });
}

export class IndexedDBAccess {
  protected _connection: IDBDatabase | null;

  constructor() {
    this._connection = null;
  }

  public dbName(): string {
    throw new Error('override dbname');
  }

  public async connect(): Promise<IDBDatabase> {
    if (this._connection) {
      db.log('open connection requested again');
      return this._connection;
    }

    this._connection = await db.openDb({
      dbName: this.dbName(),
      version: this.version(),
      upgrades: this.getDatabaseUpgrades(),
      onBlocked: () => {
        information(
          write`Database Access Blocked`,
          write`Please close all other browser tabs/windows accessing this page to continue`
        );
      },
    });

    this._connection.onversionchange = () => {
      if (this._connection) this._connection.close();
      db.log('a new version of the databse is ready, please reload this page');
      information(
        write`Application Update`,
        write`Please reload the page to continue`
      );
    };

    return this._connection;
  }

  protected async getFirstMatchingRecord<T>(
    objectStore: string,
    options?: GetOptions
  ): Promise<T | null> {
    const conn = await this.connect();
    const log = await db.get<T>(conn, objectStore, options);
    this.close();
    return log;
  }

  store(type: { name: string }): string {
    return type.name;
  }

  protected async getAll<T>(
    objectStore: string,
    options?: GetOptions
  ): Promise<T[]> {
    const conn = await this.connect();
    try {
      return db.getAll<T>(conn, objectStore, options);
    } finally {
      this.close();
    }
  }

  protected async put<T>(item: T, objectStore: string): Promise<boolean> {
    const conn = await this.connect();
    try {
      await db.put<T>(conn, objectStore, item);
      return true;
    } catch {
      return false;
    } finally {
      this.close();
    }
  }

  protected async putMany<T>(
    items: T[],
    objectStore: string
  ): Promise<boolean> {
    const conn = await this.connect();
    try {
      await db.putAll<T>(conn, objectStore, items);
      return true;
    } catch {
      return false;
    } finally {
      this.close();
    }
  }

  protected async del(id: IDBValidKey, objectStore: string): Promise<void> {
    const conn = await this.connect();
    try {
      await db.del(conn, objectStore, id);
    } finally {
      this.close();
    }
  }

  protected version(): number {
    let version = 1;
    this.getDatabaseUpgrades().forEach(
      (x) => (version = Math.max(version, x.version))
    );
    return version;
  }

  //return a list of upgrades that get this database to the correct version
  protected getDatabaseUpgrades(): db.DatabaseUpgrade[] {
    throw new Error('Method not implemented.');
  }

  protected close() {
    if (this._connection) {
      this._connection.close();
      this._connection = null;
    }
  }
}
